/**
 * Hydrates the PDP model picker with the full sibling list, bypassing the
 * 50-product cap on Liquid's collections[handle].products. Fetches
 * /collections/{slug}/products.json (paginated up to 5×250), parses each
 * product into a sibling, and rebuilds the four picker rows in place.
 *
 * The Liquid render in snippets/product-model-picker.liquid stays as the
 * instant-first-paint fallback for no-JS users and pre-hydration paint.
 *
 * Mirrors the row-building logic in product-model-picker.liquid:
 *   - Type row: only when both new + used variants exist for this model
 *   - Carrier / Color / Storage: matched on the OTHER current axes plus
 *     the current new/used flag (only the Type row flips that flag)
 */
(function () {
  'use strict';

  var picker = document.querySelector('[data-model-picker]');
  if (!picker) return;

  hydrate(picker).catch(function () {
    // Network or parse failure — drop the picker rather than leaving the
    // skeleton spinning forever, and reveal the Compatible Networks block
    // since the user no longer has a Carrier-row "i" toggle to do it.
    picker.remove();
    revealCompatibleNetworks();
  });

  async function hydrate(el) {
    var slug = el.dataset.modelSlug;
    if (!slug) return;

    var current = {
      handle:    el.dataset.currentHandle    || '',
      carrier:   el.dataset.currentCarrier   || '',
      capacity:  el.dataset.currentCapacity  || '',
      color:     el.dataset.currentColor     || '',
      size:      el.dataset.currentSize      || '',
      processor: el.dataset.currentProcessor || '',
      memory:    el.dataset.currentMemory    || '',
      isNew:     el.dataset.currentIsNew === 'true',
    };

    var configEl = el.querySelector('[data-model-picker-config]');
    if (!configEl) return;
    var config = JSON.parse(configEl.textContent);

    var products = await fetchAll(slug);
    if (!products.length) {
      el.remove();
      revealCompatibleNetworks();
      return;
    }

    var siblings = products.map(function (p) { return parseSibling(p, config); });

    // Order: Type → Screen Size → Processor → Carrier → Memory → Storage → Color.
    // Per-axis option-builders already drop empty rows (e.g. phones don't
    // have a Screen Size axis populated, laptops don't have a Carrier axis),
    // so the same row list works for every product family.
    var rows = [
      { axis: 'type',      label: 'Type',        options: buildTypeOptions(siblings, current) },
      { axis: 'size',      label: 'Screen Size', options: buildAxisOptions('size',      config.sizes      || [], siblings, current) },
      { axis: 'processor', label: 'Processor',   options: buildAxisOptions('processor', config.processors || [], siblings, current) },
      { axis: 'carrier',   label: 'Carrier',     options: buildAxisOptions('carrier',   config.carriers   || [], siblings, current) },
      { axis: 'memory',    label: 'Memory',      options: buildAxisOptions('memory',    config.memory     || [], siblings, current) },
      { axis: 'storage',   label: 'Storage',     options: buildAxisOptions('capacity',  config.storage    || [], siblings, current) },
      { axis: 'color',     label: 'Color',       options: buildAxisOptions('color',     config.colors     || [], siblings, current) },
    ];

    // Drop the Liquid-rendered rows; emit the rebuilt rows in declared order
    // so we keep Type → Carrier → Color → Storage even when JS adds rows
    // that Liquid couldn't render (siblings sat past the 50-product cap).
    var existing = el.querySelectorAll('.pdp-model-picker__row');
    for (var i = 0; i < existing.length; i++) existing[i].remove();

    var rendered = 0;
    for (var j = 0; j < rows.length; j++) {
      var row = rows[j];
      if (!row.options || !row.options.length) continue;
      el.insertBefore(buildRow(row.axis, row.label, row.options), configEl);
      rendered++;
    }

    // Picker has no axes worth showing (e.g., model has only one product)
    // — drop it entirely instead of leaving an empty wrapper behind.
    if (rendered === 0) {
      el.remove();
      revealCompatibleNetworks();
      return;
    }

    el.classList.remove('is-loading');
    wireCompatibleNetworksToggle(el);
  }

  /**
   * Compatible Networks block (rendered above the picker) is hidden by CSS
   * when a model picker is in the DOM. If a Carrier row was built, attach
   * an "i" toggle next to its label that reveals the block. If there's no
   * Carrier row, fall back to keeping the block visible — the user has no
   * other way to see it.
   */
  function wireCompatibleNetworksToggle(el) {
    var networks = document.querySelector('[data-pdp-compatible-networks]');
    if (!networks) return;
    // No tags matched server-side — nothing to reveal, skip the toggle.
    if (!networks.querySelector('.pdp-carriers')) return;

    var carrierLabel = el.querySelector('[data-axis="carrier"] .pdp-model-picker__label');
    if (!carrierLabel) {
      revealCompatibleNetworks();
      return;
    }

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'pdp-model-picker__info';
    btn.setAttribute('aria-label', 'Show compatible networks');
    btn.setAttribute('aria-expanded', 'false');
    btn.textContent = 'i';
    btn.addEventListener('click', function () {
      var open = networks.classList.toggle('is-visible');
      btn.classList.toggle('is-active', open);
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    carrierLabel.appendChild(btn);
  }

  function revealCompatibleNetworks() {
    var n = document.querySelector('[data-pdp-compatible-networks]');
    if (n) n.classList.add('is-visible');
  }

  /**
   * Pull every product in the model collection across paginated pages.
   * Hard-capped at 5 pages (1250 products) to bound the worst case.
   */
  async function fetchAll(slug) {
    var all = [];
    for (var page = 1; page <= 5; page++) {
      var url = '/collections/' + encodeURIComponent(slug) + '/products.json?limit=250&page=' + page;
      var res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) break;
      var data = await res.json();
      var products = (data && data.products) || [];
      for (var i = 0; i < products.length; i++) all.push(products[i]);
      if (products.length < 250) break;
    }
    return all;
  }

  /**
   * Project a Shopify product JSON into the shape the picker logic uses.
   * Tags arrive as a comma-separated string from /products.json; the new/used
   * flag mirrors the Liquid rule (handle suffix `-new`).
   */
  function parseSibling(p, config) {
    var tags = String(p.tags || '').split(',').map(function (t) { return t.trim(); });
    var lastSegment = String(p.handle || '').split('-').pop();
    return {
      handle:    p.handle,
      url:       '/products/' + p.handle,
      tags:      tags,
      // /collections/{slug}/products.json doesn't always include a top-level
      // `available` flag — aggregate from variants. A product is available
      // if any variant is in stock OR set to continue selling when out.
      available: isProductAvailable(p),
      isNew:     lastSegment === 'new',
      carrier:   pickFromList(tags, config.carriers   || []),
      capacity:  pickFromList(tags, config.storage    || []),
      color:     pickFromList(tags, config.colors     || []),
      size:      pickFromList(tags, config.sizes      || []),
      processor: pickFromList(tags, config.processors || []),
      memory:    pickFromList(tags, config.memory     || []),
    };
  }

  function isProductAvailable(p) {
    if (Array.isArray(p.variants) && p.variants.length) {
      for (var i = 0; i < p.variants.length; i++) {
        if (p.variants[i].available) return true;
      }
      return false;
    }
    return p.available !== false;
  }

  /**
   * Last-occurrence wins when a product carries multiple values from a list.
   * Mirrors the Liquid loops in product-model-picker.liquid that overwrite
   * current_* on each match — keeps client + server in sync if a product
   * has, say, both "Verizon" and "Verizon Unlocked" tags.
   *
   * Case-insensitive: laptops in the catalog carry "13-inch" while the
   * spec list uses "13-Inch", and various color/storage casings drift
   * across older listings. We return the canonical (list-form) value so
   * the picker label stays consistent regardless of tag-case drift.
   */
  function pickFromList(tags, list) {
    var lowerTags = tags.map(function (t) { return String(t).toLowerCase(); });
    var last = '';
    for (var i = 0; i < list.length; i++) {
      if (lowerTags.indexOf(String(list[i]).toLowerCase()) !== -1) last = list[i];
    }
    return last;
  }

  // Every axis the picker tracks. When we score a sibling for axis X, we
  // require the OTHER axes to match the current product, so the user moves
  // along one axis at a time (changing color keeps storage / processor /
  // memory the same, etc.). Axes that are blank on every sibling collapse
  // to a single-option row that's trivially preserved.
  var ALL_AXES = ['carrier', 'capacity', 'color', 'size', 'processor', 'memory'];

  /**
   * Returns an array of {value, kind, href} option descriptors for an axis.
   * `kind` is one of 'active' | 'link' | 'disabled'; `href` is set when
   * `kind === 'link'`.
   */
  function buildAxisOptions(axisKey, values, siblings, current) {
    var otherKeys = ALL_AXES.filter(function (k) { return k !== axisKey; });
    var out = [];
    for (var i = 0; i < values.length; i++) {
      var v = values[i];
      var present = siblings.some(function (s) {
        return s[axisKey] === v && s.isNew === current.isNew;
      });
      if (!present) continue;

      if (v === current[axisKey]) {
        out.push({ value: v, kind: 'active' });
        continue;
      }

      var match = siblings.find(function (s) {
        if (s[axisKey] !== v) return false;
        if (s.isNew !== current.isNew) return false;
        for (var k = 0; k < otherKeys.length; k++) {
          if (s[otherKeys[k]] !== current[otherKeys[k]]) return false;
        }
        return true;
      });
      if (match) out.push({ value: v, kind: match.available ? 'link' : 'sold-out', href: match.url });
      else       out.push({ value: v, kind: 'disabled' });
    }
    return out;
  }

  function buildTypeOptions(siblings, current) {
    var hasUsed = false, hasNew = false;
    for (var i = 0; i < siblings.length; i++) {
      if (siblings[i].isNew) hasNew = true; else hasUsed = true;
      if (hasUsed && hasNew) break;
    }
    if (!(hasUsed && hasNew)) return [];

    function findOpposite(wantNew) {
      return siblings.find(function (s) {
        if (s.isNew !== wantNew) return false;
        for (var k = 0; k < ALL_AXES.length; k++) {
          if (s[ALL_AXES[k]] !== current[ALL_AXES[k]]) return false;
        }
        return true;
      });
    }

    var out = [];
    if (current.isNew) {
      var used = findOpposite(false);
      out.push(used
        ? { value: 'Refurbished', kind: used.available ? 'link' : 'sold-out', href: used.url }
        : { value: 'Refurbished', kind: 'disabled' });
      out.push({ value: 'New', kind: 'active' });
    } else {
      out.push({ value: 'Refurbished', kind: 'active' });
      var fresh = findOpposite(true);
      out.push(fresh
        ? { value: 'New', kind: fresh.available ? 'link' : 'sold-out', href: fresh.url }
        : { value: 'New', kind: 'disabled' });
    }
    return out;
  }

  function buildRow(axisName, label, options) {
    var row = document.createElement('div');
    row.className = 'pdp-model-picker__row';
    row.setAttribute('data-axis', axisName);

    var labelEl = document.createElement('div');
    labelEl.className = 'pdp-model-picker__label';
    labelEl.textContent = label;
    row.appendChild(labelEl);

    var grid = document.createElement('div');
    grid.className = 'pdp-model-picker__grid';
    for (var i = 0; i < options.length; i++) grid.appendChild(buildOption(options[i], axisName));
    row.appendChild(grid);

    return row;
  }

  function buildOption(opt, axisName) {
    var el;
    if (opt.kind === 'link' || opt.kind === 'sold-out') {
      el = document.createElement('a');
      el.setAttribute('href', opt.href);
      el.className = 'pdp-model-picker__option' + (opt.kind === 'sold-out' ? ' is-sold-out' : '');
    } else {
      el = document.createElement('span');
      el.className = 'pdp-model-picker__option' + (opt.kind === 'active' ? ' is-active' : ' is-disabled');
      if (opt.kind === 'active')   el.setAttribute('aria-current', 'true');
      if (opt.kind === 'disabled') el.setAttribute('aria-disabled', 'true');
    }
    if (axisName === 'color') el.appendChild(buildSwatch(opt.value));
    el.appendChild(document.createTextNode(opt.value));
    return el;
  }

  /**
   * Tiny color-chip rendered alongside each Color option. The picker has no
   * structured color data — only a free-form tag like "Sierra Blue" — so we
   * infer a plausible swatch by scanning the name for a known base color
   * (last token wins, matching the "Adjective Color" naming pattern). Two-
   * tone names ("Black & White", "Rose Gold/White", "Cloud Grey and Moon
   * White", "Gold on Silver") split into a 50/50 gradient. Unknown names
   * fall back to neutral gray rather than hiding the swatch entirely.
   */
  function buildSwatch(name) {
    var sw = document.createElement('span');
    sw.className = 'pdp-model-picker__swatch';
    sw.setAttribute('aria-hidden', 'true');
    sw.style.background = colorBackground(name);
    return sw;
  }

  function colorBackground(name) {
    var clean = String(name || '').toLowerCase().replace(/\(.*?\)/g, ' ').replace(/\s+/g, ' ').trim();
    if (!clean) return SWATCH_FALLBACK;
    var parts = clean.split(/\s*\/\s*|\s+&\s+|\s+and\s+|\s+on\s+/);
    if (parts.length >= 2 && parts[0] && parts[1]) {
      return 'linear-gradient(135deg,' + inferColor(parts[0]) + ' 50%,' + inferColor(parts[1]) + ' 50%)';
    }
    return inferColor(clean);
  }

  function inferColor(text) {
    var lc = String(text).toLowerCase();
    for (var i = 0; i < COMPOUND_COLORS.length; i++) {
      if (lc.indexOf(COMPOUND_COLORS[i][0]) !== -1) return COMPOUND_COLORS[i][1];
    }
    var tokens = lc.split(/[^a-z]+/);
    for (var j = tokens.length - 1; j >= 0; j--) {
      if (tokens[j] && BASE_COLORS[tokens[j]]) return BASE_COLORS[tokens[j]];
    }
    return SWATCH_FALLBACK;
  }

  var SWATCH_FALLBACK = '#bdbdbd';

  // Compound names checked first so "Rose Gold" doesn't fall through to "gold".
  var COMPOUND_COLORS = [
    ['rose gold',      '#b76e79'],
    ['blush gold',     '#d4a574'],
    ['amber gold',     '#d4a017'],
    ['champagne gold', '#e8d8a3'],
    ['copper gold',    '#b08d57'],
    ['phantom gold',   '#c9b377'],
    ['mocha gold',     '#9a7b4f'],
    ['mystic bronze',  '#5a4d3d'],
    ['mirror gold',    '#d4af37'],
    ['matte gold',     '#b09c5d'],
    ['light gold',     '#e6cf8f'],
    ['soft gold',      '#e6cf8f'],
    ['pink gold',      '#e8c5d6'],
    ['rose pink',      '#ff8fab'],
    ['hot pink',       '#ff3399'],
    ['flamingo pink',  '#fc8eac'],
    ['cloud pink',     '#f7d1d5']
  ];

  var BASE_COLORS = {
    black:        '#1a1a1a',
    jet:          '#0a0a0a',
    jetblack:     '#0a0a0a',
    onyx:         '#0f0f0f',
    obsidian:     '#0b1215',
    ebony:        '#0c0a09',
    midnight:     '#1a2238',
    charcoal:     '#36454f',
    graphite:     '#383838',
    gunmetal:     '#2a3439',
    iron:         '#48494b',
    slate:        '#5a6c7a',
    eclipse:      '#211f1f',
    white:        '#f5f5f5',
    pearl:        '#f0ead6',
    ivory:        '#fffff0',
    porcelain:    '#f3f1ec',
    cream:        '#fffdd0',
    snow:         '#fffafa',
    chalk:        '#f0ede4',
    silver:       '#c4c8cb',
    platinum:     '#e5e4e2',
    aluminum:     '#a8a9ad',
    aluminium:    '#a8a9ad',
    titanium:     '#878680',
    gray:         '#8a8a8a',
    grey:         '#8a8a8a',
    graygreen:    '#7a8478',
    fog:          '#cfcfcf',
    mist:         '#cdd5db',
    ash:          '#a4a4a4',
    storm:        '#5e6973',
    gold:         '#d4af37',
    champagne:    '#f7e7ce',
    copper:       '#b87333',
    bronze:       '#cd7f32',
    red:          '#c0392b',
    crimson:      '#b21f35',
    cardinal:     '#c41e3a',
    maroon:       '#800020',
    burgundy:     '#6f1d2c',
    ruby:         '#9b111e',
    garnet:       '#733635',
    scarlet:      '#ff2400',
    flame:        '#e25822',
    pink:         '#ec4899',
    rose:         '#ff66cc',
    raspberry:    '#b3446c',
    blossom:      '#f7c1d2',
    peach:        '#ffcba4',
    coral:        '#ff7f50',
    coralred:     '#ff4040',
    salmon:       '#fa8072',
    orange:       '#f97316',
    tangerine:    '#ff9408',
    papaya:       '#ffefd5',
    amber:        '#ffbf00',
    yellow:       '#f1c40f',
    lime:         '#cddc39',
    lemon:        '#fff700',
    citrus:       '#e2c044',
    green:        '#2ecc71',
    sage:         '#b2ac88',
    olive:        '#808000',
    mint:         '#a6f0c6',
    teal:         '#14b8a6',
    turquoise:    '#40e0d0',
    aqua:         '#7fdbda',
    aquamarine:   '#7fffd4',
    jade:         '#00a86b',
    jadegreen:    '#00a86b',
    emerald:      '#50c878',
    forest:       '#228b22',
    pine:         '#01796f',
    aloe:         '#88aa6b',
    martian:      '#7eaf6c',
    blue:         '#2563eb',
    navy:         '#0a1f44',
    sapphire:     '#0f52ba',
    cobalt:       '#0047ab',
    sky:          '#87ceeb',
    azure:        '#007fff',
    arctic:       '#cfe6ee',
    glacier:      '#d8e8e7',
    indigo:       '#4b0082',
    ultramarine:  '#3f00ff',
    icyblue:      '#a3d8e8',
    silverblue:   '#a4b2c4',
    blueblack:    '#0d1b2a',
    abyss:        '#1c2c44',
    pacific:      '#1ca9c9',
    ocean:        '#1e6091',
    deepsea:      '#0e3b5c',
    violet:       '#8b5cf6',
    purple:       '#7c3aed',
    lavender:     '#a48dde',
    lilac:        '#c8a2c8',
    orchid:       '#da70d6',
    amethyst:     '#9966cc',
    iris:         '#5a4fcf',
    plum:         '#8e4585',
    bora:         '#a07cc8',
    brown:        '#6f4e37',
    beige:        '#d6c7a8',
    almond:       '#efdecd',
    sand:         '#c2b280',
    sandstone:    '#c19a6b',
    desert:       '#c19a6b',
    hazel:        '#8e7618',
    bay:          '#a98c66',
    natural:      '#e7d5b8',
    driftwood:    '#a89683',
    pinkgold:     '#e8c5d6',
    whitesilver:  '#dadada',
    multicolor:   'linear-gradient(135deg,#ff5f6d 0%,#ffc371 25%,#47e4af 50%,#3b82f6 75%,#a855f7 100%)',
    aurora:       'linear-gradient(135deg,#5d3fd3 0%,#ff6ec7 50%,#7afcff 100%)'
  };
})();
